import React, { FC, useState } from 'react';
import { User, MapPin, CreditCard, ArrowRightLeft, FileText, Banknote } from 'lucide-react';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { Dropdown, DropdownItem } from 'components/forms/Dropdown';
import { BankAccountDto } from 'api/bank-account';
import { BankAccount } from 'components/domain/Bank/BankAccount';

const InfoItem: FC<{
  icon?: React.ReactNode;
  label: string;
  value: string | number | null | undefined;
}> = ({ icon, label, value = false }) => {
  if (!value && value !== 0) return null;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg transition-all duration-200 hover:bg-primary/5  bg-gray-50/50`}>
      {
        icon &&
        <div className="flex-shrink-0 w-5 h-5 text-primary/70 mt-0.5">
          {icon}
        </div>
      }
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
        <p className={`text-sm break-words text-gray-900`}>
          {value}
        </p>
      </div>
    </div>
  );
};

interface PayerDto {
  name: string; // Nom, prénom ou raison sociale
  address: string;
  addressComplement?: string;
  city: string;
  postalCode: string;
}

const PayerInfoView: FC<{ payer: PayerDto }> = ({ payer }) => {
  return (
    <div className="space-y-6">
      <CardsSection title="Nom et adresse du payeur">
        <div className="space-y-4">
          {/* Nom / Raison sociale */}
          <InfoItem
            icon={<User />}
            label="Nom, prénom ou raison sociale"
            value={payer.name}
          />
          {/* Adresse complète */}
          <div className="grid grid-cols-4 gap-4">
            <InfoItem
              icon={<MapPin />}
              label="Adresse"
              value={payer.address}
            />
            <InfoItem
              label="Complément d'adresse"
              value={payer.addressComplement}
            />
            <InfoItem
              label="Ville"
              value={payer.city}
            />
            <InfoItem
              label="Code postal"
              value={payer.postalCode}
            />
          </div>
        </div>
      </CardsSection>
    </div>
  );
};

const PaymentMethod: FC = () => {
  const items: DropdownItem[] = [
    {
      key: 'SEPA',
      label: 'Prélèvement',
      icon: <CreditCard />
    },
    {
      key: 'CARD',
      label: 'Carte bancaire',
      icon: <CreditCard />
    },
    {
      key: 'TRANSFER',
      label: 'Virement',
      icon: <ArrowRightLeft />
    },
    {
      key: 'CHECK',
      label: 'Chèque',
      icon: <FileText />
    },
    {
      key: 'CASH',
      label: 'Espèces',
      icon: <Banknote />
    }
  ];

  return (
    <CardsSection title="Moyen de paiement">
      <Dropdown
        name="paymentMethod"
        items={items}
        selectedItem={items[0]}
      />
    </CardsSection>
  );
};

const DebitAccount: FC = () => {
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>("1");

  const mockBankAccounts: BankAccountDto[] = [
    {
      id: "1",
      personId: '',
      iban: "FR 76 1234 8000 0198 0065 1235 087",
      label: "AMBRE DERAS",
      isDefault: false,
      isSelected: false,
      creationTime: new Date(),
    },
    {
      id: "2",
      personId: '',
      iban: "FR 76 1234 8000 0198 0065 1235 087",
      label: "AMBRE DERAS",
      isDefault: false,
      isSelected: false,
      creationTime: new Date(),
    },
    {
      id: "3",
      personId: '',
      iban: "FR 89 5678 9000 0234 0098 7654 321",
      label: "JEAN DUPONT",
      isDefault: false,
      isSelected: false,
      creationTime: new Date(),
    },
    {
      id: "4",
      personId: '',
      iban: "FR 45 9876 5432 1098 7654 3210 456",
      label: "MARIE MARTIN",
      isDefault: false,
      isSelected: false,
      creationTime: new Date(),
    },
  ];

  const handleSelect = (accountId: string) => {
    setSelectedAccountId(accountId);
  };

  return (
    <CardsSection title="Compte a debiter">
      <div className="flex flex-col gap-3">
        {mockBankAccounts.map((account) => (
          <BankAccount
            key={account.id}
            bankAccount={account}
            isSelected={account.id === selectedAccountId}
            onSelect={() => handleSelect(account.id)}
            onDelete={() => handleSelect(account.id)}
          />
        ))}
      </div>
    </CardsSection>
  );
};

export const mockPayer: PayerDto = {
  name: "SCI Durand",
  address: "30 rue Larmeroux",
  addressComplement: "Au fond de la cour",
  city: "Montigny le Bretonneux",
  postalCode: "78000"
};

// Main Component
const PaymentDetails: FC = () => {
  return (
    <div className='space-y-4'>
      <PayerInfoView payer={mockPayer} />
      <PaymentMethod />
      <DebitAccount />
    </div>
  );
};

export default PaymentDetails;
